'use client'

import { useState } from 'react'

interface QRCodeModalProps {
  shortCode: string
  shortUrl: string
  onClose: () => void
}

export function QRCodeModal({ shortCode, shortUrl, onClose }: QRCodeModalProps) {
  const [size, setSize] = useState(300)
  const [format, setFormat] = useState<'png' | 'svg'>('png')

  const qrUrl = `/api/qr/${shortCode}?size=${size}&format=${format}`

  const downloadQR = () => {
    const link = document.createElement('a')
    link.href = qrUrl
    link.download = `${shortCode}-qr.${format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">QR Code</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex justify-center mb-6 bg-gray-100 dark:bg-gray-700 p-6 rounded-lg">
          <img
            src={qrUrl}
            alt={`QR code for ${shortUrl}`}
            className="max-w-full"
            style={{ width: `${size}px`, height: `${size}px` }}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Size: {size}x{size}px
          </label>
          <input
            type="range"
            min="150"
            max="500"
            step="50"
            value={size}
            onChange={(e) => setSize(parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Format
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setFormat('png')}
              className={`px-4 py-2 rounded-lg transition ${
                format === 'png'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              PNG
            </button>
            <button
              onClick={() => setFormat('svg')}
              className={`px-4 py-2 rounded-lg transition ${
                format === 'svg'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              SVG
            </button>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={downloadQR}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
          >
            Download QR
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold py-3 px-6 rounded-lg transition"
          >
            Close
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
          Scan to visit: <span className="font-mono">{shortUrl}</span>
        </p>
      </div>
    </div>
  )
}
